<template>
    <div class="form-dashboard">
        <div id="FormList">
            <div class="header">
                <div class="header-logo">
                    <span class="md-icon">description</span>
                </div>
                <div class="header-text">
                    <h1>{{ $t("form.forms") }}</h1>
                    <p>{{ $t("form.formsDesc") }}</p>
                </div>
            </div>
            <div class="container">
                <!-- Loading State -->
                <div v-if="loading" class="loading-container">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">Formlar yükleniyor...</p>
                </div>

                <!-- Form List -->
                <div v-else-if="formsWithColors.length > 0" class="form-grid">
                    <div class="list-card" :class="{
                        selected: selectedForm && selectedForm._id === form._id,
                        'limit-exceeded': features.maxFormViewLimit && form.formViews >= features.maxFormViewLimit
                    }" v-for="form in formsWithColors" :key="form._id" :style="{ '--border-color': form.color }"
                        @click="formId(form._id)" v-tippy="features.maxFormViewLimit && form.formViews >= features.maxFormViewLimit ? {
                            content: 'Bu formunuzun görüntüleme limiti aşıldı. Lütfen yüksek plan seçiniz.',
                            placement: 'top',
                            theme: 'error'
                        } : {}">
                    <div class="icon-section" :style="{ backgroundColor: form.color }" :data-count="form.unreadCount"
                        v-if="form.unreadCount">
                        <i class="md-icon">article</i>
                    </div>
                    <div class="icon-section" :style="{ backgroundColor: form.color }" v-else>
                        <i class="md-icon">article</i>
                    </div>
                    <div class="text-section">
                        <div class="title">{{ form.title }}</div>
                    </div>
                    <div class="action-section">
                        <button class="action-btn edit" @click="editForm(form._id)"
                            v-tippy="{ content: $t('message.edit'), placement: 'top' }">
                            <i class="md-icon">edit</i>
                        </button>
                        <button class="action-btn inbox" @click="inboxForm(form._id)"
                            v-tippy="{ content: $t('form.inbox'), placement: 'top' }">
                            <i class="md-icon">inbox</i>
                        </button>
                        <button class="action-btn delete" @click="showDeleteModal(form)"
                            v-tippy="{ content: $t('message.delete'), placement: 'top' }">
                            <i class="md-icon">delete</i>
                        </button>
                    </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <div class="empty-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14,2 14,8 20,8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10,9 9,9 8,9"/>
                        </svg>
                    </div>
                    <h3>Henüz form oluşturmadınız</h3>
                    <p>Yeni bir form oluşturarak başlayın</p>
                </div>
            </div>
        </div>

        <div class="form-details" v-if="selectedForm">
            <div class="details-header">
                <h2>{{ selectedForm.title || '-' }}</h2>
                <div class="header-actions">
                    <button class="preview-btn" @click="viewForm(selectedForm._id)">
                        <i class="md-icon">visibility</i>
                        {{ $t("form.formView") }}
                    </button>
                    <button class="edit-btn" @click="editForm(selectedForm._id)">
                        <i class="md-icon">edit</i>
                        {{ $t("form.formEdit") }}
                    </button>
                </div>
            </div>

            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-icon total">
                        <i class="md-icon">summarize</i>
                    </div>
                    <div class="stat-info">
                        <span class="stat-label">{{ $t("form.totalResponse") }}</span>
                        <span class="stat-value">{{ selectedForm.total || 0 }}</span>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-icon unread">
                        <i class="md-icon">mark_email_unread</i>
                    </div>
                    <div class="stat-info">
                        <span class="stat-label">{{ $t("form.totalUnreadResponse") }}</span>
                        <span class="stat-value">{{ selectedForm.unreadCount || 0 }}</span>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-icon read">
                        <i class="md-icon">mark_email_read</i>
                    </div>
                    <div class="stat-info">
                        <span class="stat-label">{{ $t("form.totalReadResponse") }}</span>
                        <span class="stat-value">{{ selectedForm.readCount || 0 }}</span>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-icon archived">
                        <i class="md-icon">inventory_2</i>
                    </div>
                    <div class="stat-info">
                        <span class="stat-label">{{ $t("form.totalArchivedResponse") }}</span>
                        <span class="stat-value">{{ selectedForm.archivedCount || 0 }}</span>
                    </div>
                </div>
            </div>
        </div>

        <DeleteModal v-if="deleteModal" :description="$t('form.deleteFormConfirm')"
            :btnCancelText="$t('message.cancel')" :btnoktext="$t('message.delete')" @cancel="deleteModal = false"
            @confirm="confirmDelete" />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DeleteModal from "../../Tasks/components/DeleteModal.vue";

export default {
    name: "FormList",
    components: {
        DeleteModal,
    },
    data() {
        return {
            deleteModal: false,
            selectedForm: null,
            formToDelete: null,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            forms: (state) => state.forms.forms,
            features: (state) => state.site.features,
        }),
        formsWithColors() {
            return this.forms.map((form) => {
                return {
                    ...form,
                    color: this.getRandomColor(form._id),
                };
            });
        },
        selectedFormColor() {
            return this.selectedForm ? this.getRandomColor(this.selectedForm._id) : "rgb(255, 179, 153)";
        },
    },
    methods: {
        ...mapActions({
            getListForm: "forms/getListForm",
            deleteForm: "forms/deleteForm",
        }),
        getRandomColor(seed) {
            const colors = [
                "#8B0000",
                "#A52A2A",
                "#800000",
                "#2F4F4F",
                "#556B2F",
                "#8B4513",
                "#483D8B",
                "#2E8B57",
                "#4682B4",
                "#6A5ACD",
                "#4B0082",
                "#708090",
                "#696969",
                "#808080",
                "#2F4F4F",
                "#3CB371",
                "#8B008B",
                "#9932CC",
                "#8B0000",
                "#8B4513",
                "#2E8B57",
                "#556B2F",
                "#6A5ACD",
                "#483D8B",
                "#4B0082",
                "#2F4F4F",
                "#696969",
                "#708090",
                "#808080",
                "#8B0000",
                "#A52A2A",
                "#800000",
                "#2F4F4F",
                "#556B2F",
                "#8B4513",
                "#483D8B",
                "#2E8B57",
                "#4682B4",
                "#6A5ACD",
                "#4B0082",
                "#708090",
                "#696969",
                "#808080",
                "#2F4F4F",
                "#3CB371",
            ];
            let hash = 0;
            for (let i = 0; i < seed.length; i++) {
                hash = seed.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
        async fetchForms() {
            this.loading = true;
            try {
                await this.getListForm({ siteId: this.site._id });
                if (this.forms && this.forms.length > 0) {
                    this.selectedForm = this.forms[0];
                }
            } catch (error) {
                console.error("Form listesi yüklenemedi:", error);
            } finally {
                this.loading = false;
            }
        },
        formId(formId) {
            this.selectedForm = this.forms.find((form) => form._id === formId);
        },
        async editForm(formId) {
            this.$router.push("form/build/" + formId);
        },
        async inboxForm(form) {
            this.$router.push({
                path: `/form/inbox/${form}`,
            });
        },
        showDeleteModal(form) {
            this.formToDelete = form;
            this.deleteModal = true;
        },
        async confirmDelete() {
            if (this.formToDelete) {
                await this.deleteForm({ siteId: this.site._id, formId: this.formToDelete._id });
                this.$message.success("Form çöp kutusuna taşındı");
                await this.fetchForms();
                this.formToDelete = null;
            }
            this.deleteModal = false;
        },
        viewForm(formId) {
            window.open(`https://form.supsis.ai/form/${formId}/${this.site._id}`, "_blank");
        },
    },
    async created() {
        await this.fetchForms();
    },
};
</script>

<style lang="scss" scoped>
.form-dashboard {
    display: flex;
    gap: clamp(1.5rem, 3vw, 2rem);
    padding: clamp(1rem, 2vw, 1.5rem);
    min-height: 100%;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

#FormList {
    flex: 1;
    max-width: 800px;
    min-width: 0;

    .header {
        display: flex;
        align-items: center;
        padding: clamp(1.25rem, 2.5vw, 1.75rem);
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px);
        border-radius: clamp(1rem, 2vw, 1.25rem);
        box-shadow: 0 10px 30px rgba(242, 106, 82, 0.08),
                    0 4px 15px rgba(0, 0, 0, 0.04);
        margin-bottom: clamp(1.25rem, 2.5vw, 1.75rem);
        border: 1px solid rgba(255, 255, 255, 0.2);

        .header-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            width: clamp(3rem, 6vw, 3.5rem);
            height: clamp(3rem, 6vw, 3.5rem);
            background: linear-gradient(135deg, #f26a52 0%, #e55a42 100%);
            border-radius: clamp(0.75rem, 1.5vw, 1rem);
            margin-right: clamp(0.75rem, 1.5vw, 1.25rem);
            box-shadow: 0 4px 15px rgba(242, 106, 82, 0.2);

            .md-icon {
                font-size: clamp(1.25rem, 2.5vw, 1.5rem);
                color: white;
            }
        }

        .header-text {
            h1 {
                font-size: clamp(1.25rem, 2.5vw, 1.625rem);
                font-weight: 700;
                color: rgba(55, 65, 81, 0.95);
                margin: 0;
                letter-spacing: -0.01em;
            }

            p {
                font-size: clamp(0.8rem, 1.6vw, 0.9rem);
                color: rgba(107, 114, 128, 0.8);
                margin: clamp(0.25rem, 0.5vw, 0.375rem) 0 0;
                line-height: 1.4;
            }
        }
    }

    .container {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px);
        border-radius: clamp(1rem, 2vw, 1.25rem);
        box-shadow: 0 10px 30px rgba(242, 106, 82, 0.08),
                    0 4px 15px rgba(0, 0, 0, 0.04);
        padding: clamp(1rem, 2vw, 1.5rem);
        border: 1px solid rgba(255, 255, 255, 0.2);
        overflow: hidden;

        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: clamp(3rem, 6vw, 4rem);

            .loading-spinner {
                width: clamp(2.5rem, 5vw, 3rem);
                height: clamp(2.5rem, 5vw, 3rem);
                border: 3px solid rgba(242, 106, 82, 0.2);
                border-top: 3px solid #f26a52;
                border-radius: 50%;
                animation: spin 1s linear infinite;
                margin-bottom: clamp(1rem, 2vw, 1.5rem);
            }

            .loading-text {
                font-size: clamp(0.9rem, 1.8vw, 1rem);
                color: rgba(107, 114, 128, 0.8);
                font-weight: 500;
                margin: 0;
            }
        }

        .form-grid {
            display: grid;
            gap: clamp(0.75rem, 1.5vw, 1rem);
            max-height: 70vh;
            overflow-y: auto;
            padding-right: clamp(0.25rem, 0.5vw, 0.5rem);

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-track {
                background: rgba(248, 250, 252, 0.8);
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb {
                background: rgba(242, 106, 82, 0.3);
                border-radius: 3px;

                &:hover {
                    background: rgba(242, 106, 82, 0.5);
                }
            }
        }

        .list-card {
            display: flex;
            align-items: center;
            padding: clamp(0.875rem, 1.75vw, 1.25rem);
            border-radius: clamp(0.75rem, 1.5vw, 1rem);
            border: 1px solid rgba(226, 232, 240, 0.8);
            border-left-width: 4px;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(10px);
            border-left-color: var(--border-color);
            cursor: pointer;

            &:hover {
                transform: translateY(-2px) translateX(2px);
                box-shadow: 0 10px 25px rgba(242, 106, 82, 0.1),
                            0 4px 15px rgba(0, 0, 0, 0.05);
                background: rgba(255, 255, 255, 0.95);
                border-color: rgba(242, 106, 82, 0.3);
            }

            &.selected {
                background: linear-gradient(145deg,
                    rgba(242, 106, 82, 0.05) 0%,
                    rgba(242, 106, 82, 0.02) 100%);
                border-left-width: 6px;
                border-left-color: #f26a52;
                box-shadow: 0 8px 25px rgba(242, 106, 82, 0.15);
            }

            &.limit-exceeded {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                box-shadow: 0 0 12px rgba(239, 68, 68, 0.3);
                animation: pulseBorder 2s infinite;
                border-left-color: #ef4444 !important;
            }

            .icon-section {
                width: clamp(2.5rem, 5vw, 3rem);
                height: clamp(2.5rem, 5vw, 3rem);
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: clamp(0.5rem, 1vw, 0.75rem);
                margin-right: clamp(0.875rem, 1.75vw, 1.25rem);
                position: relative;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

                i {
                    color: white;
                    font-size: clamp(1.125rem, 2.25vw, 1.375rem);
                }

                &[data-count]:not([data-count="0"]):not([data-count=""])::after {
                    content: attr(data-count);
                    position: absolute;
                    top: -6px;
                    right: -6px;
                    background: #ef4444;
                    color: white;
                    font-size: clamp(0.7rem, 1.4vw, 0.8rem);
                    font-weight: 600;
                    min-width: clamp(1.25rem, 2.5vw, 1.5rem);
                    height: clamp(1.25rem, 2.5vw, 1.5rem);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid white;
                    padding: 0 clamp(0.125rem, 0.25vw, 0.25rem);
                    animation: scaleIn 0.3s ease;
                    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
                }
            }

            .text-section {
                flex: 1;
                display: flex;
                align-items: center;
                min-width: 0;

                .title {
                    font-size: clamp(0.9rem, 1.8vw, 1rem);
                    font-weight: 600;
                    color: rgba(55, 65, 81, 0.9);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 1.4;
                }
            }

            .action-section {
                display: flex;
                gap: clamp(0.375rem, 0.75vw, 0.5rem);
                margin-left: clamp(0.75rem, 1.5vw, 1rem);

                .action-btn {
                    width: clamp(2rem, 4vw, 2.25rem);
                    height: clamp(2rem, 4vw, 2.25rem);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid rgba(226, 232, 240, 0.8);
                    border-radius: clamp(0.375rem, 0.75vw, 0.5rem);
                    background: rgba(255, 255, 255, 0.9);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    backdrop-filter: blur(5px);

                    i {
                        font-size: clamp(1rem, 2vw, 1.125rem);
                    }

                    &:hover {
                        background: rgba(255, 255, 255, 1);
                        transform: scale(1.05);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    }

                    &.edit {
                        i {
                            color: #f26a52;
                        }

                        &:hover {
                            border-color: rgba(242, 106, 82, 0.3);
                            background: rgba(255, 247, 244, 0.95);
                        }
                    }

                    &.inbox {
                        i {
                            color: #8b5cf6;
                        }

                        &:hover {
                            border-color: rgba(139, 92, 246, 0.3);
                            background: rgba(245, 243, 255, 0.95);
                        }
                    }

                    &.delete {
                        i {
                            color: #ef4444;
                        }

                        &:hover {
                            border-color: rgba(239, 68, 68, 0.3);
                            background: rgba(254, 242, 242, 0.95);
                        }
                    }
                }
            }
        }

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: clamp(3rem, 6vw, 4rem);
            text-align: center;

            .empty-icon {
                width: clamp(4rem, 8vw, 5rem);
                height: clamp(4rem, 8vw, 5rem);
                background: rgba(242, 106, 82, 0.1);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: clamp(1.25rem, 2.5vw, 1.75rem);

                svg {
                    width: clamp(2rem, 4vw, 2.5rem);
                    height: clamp(2rem, 4vw, 2.5rem);
                    color: #f26a52;
                }
            }

            h3 {
                font-size: clamp(1.125rem, 2.25vw, 1.375rem);
                font-weight: 600;
                color: rgba(55, 65, 81, 0.9);
                margin: 0 0 clamp(0.5rem, 1vw, 0.75rem) 0;
            }

            p {
                font-size: clamp(0.875rem, 1.75vw, 1rem);
                color: rgba(107, 114, 128, 0.8);
                margin: 0;
                line-height: 1.5;
            }
        }
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes scaleIn {
    0% {
        transform: scale(0);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes pulseBorder {
    0% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    70% {
        box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
}

.form-details {
    width: clamp(320px, 30vw, 400px);
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: clamp(1rem, 2vw, 1.25rem);
    box-shadow: 0 10px 30px rgba(242, 106, 82, 0.08),
                0 4px 15px rgba(0, 0, 0, 0.04);
    padding: clamp(1.25rem, 2.5vw, 1.75rem);
    height: fit-content;
    border: 1px solid rgba(255, 255, 255, 0.2);

    .details-header {
        margin-bottom: clamp(1.5rem, 3vw, 2rem);

        h2 {
            font-size: clamp(1.125rem, 2.25vw, 1.375rem);
            font-weight: 700;
            color: rgba(55, 65, 81, 0.95);
            margin: 0 0 clamp(0.75rem, 1.5vw, 1rem) 0;
            letter-spacing: -0.01em;
        }

        .header-actions {
            display: flex;
            gap: clamp(0.5rem, 1vw, 0.75rem);

            button {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: clamp(0.375rem, 0.75vw, 0.5rem);
                padding: clamp(0.625rem, 1.25vw, 0.875rem) clamp(0.75rem, 1.5vw, 1rem);
                border: 1px solid rgba(226, 232, 240, 0.8);
                border-radius: clamp(0.5rem, 1vw, 0.75rem);
                font-size: clamp(0.8rem, 1.6vw, 0.9rem);
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
                backdrop-filter: blur(5px);

                i {
                    font-size: clamp(1rem, 2vw, 1.125rem);
                }
            }

            .preview-btn {
                background: rgba(248, 250, 252, 0.9);
                color: rgba(71, 85, 105, 0.9);

                &:hover {
                    background: rgba(241, 245, 249, 0.95);
                    border-color: rgba(203, 213, 225, 0.8);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                }
            }

            .edit-btn {
                background: linear-gradient(135deg, #f26a52 0%, #e55a42 100%);
                color: white;
                border-color: rgba(242, 106, 82, 0.3);

                &:hover {
                    background: linear-gradient(135deg, #e55a42 0%, #d44a32 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(242, 106, 82, 0.3);
                }
            }
        }
    }

    .stats-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: clamp(0.75rem, 1.5vw, 1rem);

        .stat-card {
            background: rgba(248, 250, 252, 0.6);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(226, 232, 240, 0.8);
            border-radius: clamp(0.75rem, 1.5vw, 1rem);
            padding: clamp(0.875rem, 1.75vw, 1.25rem);
            display: flex;
            align-items: center;
            gap: clamp(0.75rem, 1.5vw, 1rem);
            transition: all 0.3s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.8);
                border-color: rgba(203, 213, 225, 0.8);
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
            }

            .stat-icon {
                width: clamp(2.5rem, 5vw, 3rem);
                height: clamp(2.5rem, 5vw, 3rem);
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: clamp(0.5rem, 1vw, 0.75rem);
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

                i {
                    font-size: clamp(1.125rem, 2.25vw, 1.375rem);
                    color: white;
                }

                &.total {
                    background: linear-gradient(135deg, #f26a52 0%, #e55a42 100%);
                }

                &.unread {
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
                }

                &.read {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }

                &.archived {
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                }
            }

            .stat-info {
                display: flex;
                flex-direction: column;
                gap: clamp(0.125rem, 0.25vw, 0.25rem);
                flex: 1;

                .stat-label {
                    font-size: clamp(0.7rem, 1.4vw, 0.8rem);
                    color: rgba(107, 114, 128, 0.8);
                    font-weight: 500;
                    line-height: 1.3;
                }

                .stat-value {
                    font-size: clamp(1rem, 2vw, 1.25rem);
                    font-weight: 700;
                    color: rgba(55, 65, 81, 0.95);
                    line-height: 1.2;
                }
            }
        }
    }
}

@media screen and (max-width: 1366px) {
    .form-dashboard {
        padding: 1rem;
    }

    #FormList {
        max-width: 700px;
    }

    .form-details {
        width: 340px;
    }
}

@media screen and (max-width: 1024px) {
    .form-dashboard {
        flex-direction: column;
    }

    #FormList {
        max-width: none;
    }

    .form-details {
        width: 100%;
    }
}

@keyframes scaleIn {
    0% {
        transform: scale(0);
    }

    50% {
        transform: scale(1.2);
    }

    100% {
        transform: scale(1);
    }
}

@keyframes pulseBorder {
    0% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
}
</style>
